import {
  profileRetrievePerson,
  profileRetrievePropertyDetails,
  profileRetrievePetDetails,
  profileRetrieveVehicleDrivers,
  profileRetrieveVehicles,
} from '../api/apiServices';

const ACCOUNT_DATA_CACHE_KEY = 'accountDataCache';
export const ACCOUNT_DATA_UPDATED_EVENT = 'hippo:account-data-updated';

export const safeParse = (raw, fallback = null) => {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const normalizeCellNumber = (cell) => {
  if (!cell) return '';
  let value = String(cell).trim().replace(/\s+/g, '');
  if (value.startsWith('+27')) value = `0${value.slice(3)}`;
  if (value.startsWith('27')) value = `0${value.slice(2)}`;
  return value;
};

const dispatchAccountDataUpdated = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(ACCOUNT_DATA_UPDATED_EVENT));
};

const readRawCache = () =>
  safeParse(localStorage.getItem(ACCOUNT_DATA_CACHE_KEY), {});

const isMeaningfulObject = (value) =>
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.keys(value).length > 0;

const normalizeArrayResponse = (response, keys = []) => {
  if (Array.isArray(response)) return response;
  if (!response || typeof response !== 'object') return [];

  for (const key of keys) {
    if (Array.isArray(response[key])) return response[key];
  }

  return [];
};

const normalizeObjectResponse = (response) =>
  isMeaningfulObject(response) ? response : null;

const normalizeSnapshot = (snapshot = {}) => ({
  cellNumber: normalizeCellNumber(snapshot?.cellNumber),
  syncedAt: snapshot?.syncedAt || Date.now(),
  person: normalizeObjectResponse(snapshot?.person),
  vehicles: Array.isArray(snapshot?.vehicles) ? snapshot.vehicles : [],
  drivers: Array.isArray(snapshot?.drivers) ? snapshot.drivers : [],
  pets: Array.isArray(snapshot?.pets) ? snapshot.pets : [],
  properties: normalizeObjectResponse(snapshot?.properties),
});

export const readAccountDataCache = (cellNumber = '') => {
  const snapshot = normalizeSnapshot(readRawCache());
  if (!snapshot.cellNumber) return null;

  const normalizedCellNumber = normalizeCellNumber(cellNumber);
  if (normalizedCellNumber && snapshot.cellNumber !== normalizedCellNumber) {
    return null;
  }

  return snapshot;
};

export const writeAccountDataCache = (snapshot) => {
  const normalized = normalizeSnapshot({
    ...readRawCache(),
    ...snapshot,
  });

  localStorage.setItem(ACCOUNT_DATA_CACHE_KEY, JSON.stringify(normalized));
  dispatchAccountDataUpdated();
  return normalized;
};

export const clearAccountDataCache = () => {
  localStorage.removeItem(ACCOUNT_DATA_CACHE_KEY);
  dispatchAccountDataUpdated();
};

const mergeSettledValue = (settled, fallback) =>
  settled.status === 'fulfilled' ? settled.value : fallback;

export async function syncAccountData(
  cellNumber,
  {
    seedPerson = null,
    seedVehicles = null,
    seedDrivers = null,
    seedPets = null,
    seedProperties = null,
  } = {}
) {
  const normalizedCellNumber = normalizeCellNumber(cellNumber);
  if (!normalizedCellNumber) return null;

  const previous = readAccountDataCache(normalizedCellNumber) || {};

  const [
    personResult,
    vehiclesResult,
    driversResult,
    propertiesResult,
    petsResult,
  ] = await Promise.allSettled([
    seedPerson
      ? Promise.resolve(seedPerson)
      : profileRetrievePerson(normalizedCellNumber),
    seedVehicles != null
      ? Promise.resolve(seedVehicles)
      : profileRetrieveVehicles(normalizedCellNumber),
    seedDrivers != null
      ? Promise.resolve(seedDrivers)
      : profileRetrieveVehicleDrivers(normalizedCellNumber),
    seedProperties != null
      ? Promise.resolve(seedProperties)
      : profileRetrievePropertyDetails(normalizedCellNumber),
    seedPets != null
      ? Promise.resolve(seedPets)
      : profileRetrievePetDetails(normalizedCellNumber),
  ]);

  const freshPerson = mergeSettledValue(personResult, null);
  const mergedPerson = freshPerson
    ? { ...previous.person, ...freshPerson }
    : previous.person;

  // The profile API does not return IDNumber or FullAddress. Fill them in from
  // formValues if the merged person is still missing them.
  if (mergedPerson) {
    const pickString = (...candidates) =>
      candidates.find((v) => typeof v === 'string' && v.trim().length > 0) ||
      '';

    const fv = safeParse(localStorage.getItem('formValues'), {});

    if (!pickString(mergedPerson.IDNumber)) {
      const id = pickString(fv?.IDNumber, fv?.IdNumber, fv?.idNumber);
      if (id) mergedPerson.IDNumber = id;
    }

    if (!pickString(mergedPerson.FullAddress)) {
      const addr = pickString(fv?.FullAddress, fv?.fullAddress, fv?.address);
      if (addr) mergedPerson.FullAddress = addr;
    }
  }

  const nextSnapshot = normalizeSnapshot({
    cellNumber: normalizedCellNumber,
    syncedAt: Date.now(),
    person: mergedPerson,
    vehicles: normalizeArrayResponse(
      mergeSettledValue(vehiclesResult, previous.vehicles),
      ['Vehicles']
    ),
    drivers: normalizeArrayResponse(
      mergeSettledValue(driversResult, previous.drivers),
      ['Drivers']
    ),
    properties: normalizeObjectResponse(
      mergeSettledValue(propertiesResult, previous.properties)
    ),
    pets: normalizeArrayResponse(mergeSettledValue(petsResult, previous.pets), [
      'Pets',
    ]),
  });

  const writtenSnapshot = writeAccountDataCache(nextSnapshot);

  return writtenSnapshot;
}
